import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getProductListApi } from '~/features/product/api/get-product-list.api';
import { getProductApi } from '~/features/product/api/get-product.api';

import type { PaginatedApiResponse } from '~/common/types/api.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type { Product, ProductListParams } from '~/features/product/types/product.type';

export interface ProductState {
  isLoading: boolean;
  list: Product[];
  meta: PaginationMeta | null;
  params: ProductListParams | null;
  selectedProduct: Product | null;
}

const initialState: ProductState = {
  isLoading: false,
  list: [],
  meta: null,
  params: null,
  selectedProduct: null,
};

/**
 * 제품 목록 조회
 */
export const fetchProductList = createAsyncThunk<
  PaginatedApiResponse<Product>,
  ProductListParams | undefined
>('product/fetchList', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getProductListApi(params);
    return data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * 제품 상세 조회
 */
export const fetchProductDetail = createAsyncThunk<Product, number>(
  'product/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await getProductApi(id);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const ProductSlice = createSlice({
  initialState,
  name: 'product',
  reducers: {
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
    },
    resetSelectedProduct: state => {
      state.selectedProduct = null;
    },
    resetProduct: state => {
      state.selectedProduct = null;
      state.list = [];
      state.meta = null;
      state.params = null;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // fetchProductList
      .addCase(fetchProductList.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg ?? null;
      })
      .addCase(fetchProductList.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.meta = action.payload.meta ?? null;
        state.isLoading = false;
      })
      .addCase(fetchProductList.rejected, state => {
        state.isLoading = false;
      })
      // fetchProductDetail
      .addCase(fetchProductDetail.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchProductDetail.fulfilled, (state, action) => {
        state.selectedProduct = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchProductDetail.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const { setSelectedProduct, resetSelectedProduct, resetProduct } = ProductSlice.actions;
export default ProductSlice;
